import React from "react";

import cx from "classnames";

import { THEME_COLORS } from "../../theme/themeTokens";

import styles from "./Loader.scss";

const DEFAULT_COLOR = THEME_COLORS.interactiveDefault;

/**
 * Size options for the Loader component
 */
type LoaderSize = "default" | "small" | "extraSmall";

/**
 * Props for the Loader component
 */
export interface LoaderProps {
  /**
   * Size of the Loader component
   * @default "default"
   */
  size?: LoaderSize;

  /**
   * Color of the stroke of the Loader
   * @default "systemDefault"
   */
  color?: string;

  /**
   * Loader horizontally centered with a margin-top applied
   * @default false
   */
  centered?: boolean;

  /**
   * Loader horizontally centered. No margin-top applied
   * @default false
   */
  horizontallyCentered?: boolean;

  /**
   * Only SVG for embedding rendered
   * @default false
   */
  nowrapper?: boolean;

  /**
   * Prop used to test this Loader. It will render as a data-testid prop
   */
  testId?: string;
}

/**
 * A Loading Icon.
 */
export function Loader(props: LoaderProps) {
  const {
    size = "default",
    centered = false,
    color = DEFAULT_COLOR,
    horizontallyCentered = false,
    nowrapper = false,
  } = props;

  const getSizeModifier = (): string => {
    return cx({
      [styles["loader--small"]]: size === "small",
      [styles["loader--default"]]: size === "default",
      [styles["loader--extra-small"]]: size === "extraSmall",
    });
  };

  const loaderClassName = cx(styles["loading-overlay"], getSizeModifier());

  const wrapper = cx({
    [styles["loader--center"]]: centered,
    [styles["loader--horizontallyCentered"]]: horizontallyCentered,
  });

  const spinner = (
    <svg className={styles.circular} viewBox="25 25 50 50">
      <circle
        className={styles.path}
        cx="50"
        cy="50"
        r="20"
        fill="none"
        strokeWidth="5"
        strokeMiterlimit="10"
        stroke={color}
      />
    </svg>
  );

  if (nowrapper) {
    return spinner;
  } else {
    return (
      <div className={wrapper} data-testid={props.testId}>
        <div className={loaderClassName}>
          <div className={styles.loader}>{spinner}</div>
        </div>
      </div>
    );
  }
}
